import { FC, useContext, useState } from "react";

import { TranslationContext } from "@/app/providers";
import { GetLanguageValueAsString, Translate } from "@/app/application/Translate";
import {
	getTextColorForBackground,
	transformColorForTheme,
} from "@/features/procedures/lib/checklist";
import { ProcedureNodeStep } from "@/features/procedures/model/types/checklist";
import { CheckpointType } from "@/features/procedures/model/types/manualChecklist";
import { ChecklistStatus } from "@/features/procedures/ui/components/checklists/checklist/ChecklistStatus";
import { AddComment } from "@/features/procedures/ui/components/checklists/checklist/step/AddComment";
import { StyledMenu } from "@/shared/ui/StyledMenu";
import { Language } from "@/model/Language/Language";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import {
	Box,
	CircularProgress,
	IconButton,
	ListItemIcon,
	ListItemText,
	MenuItem,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";

import { LiveStepStatus } from "./LiveStepStatus";

interface StepToolsProps {
	checklistId: number | undefined;
	step: ProcedureNodeStep;
	toggleShowMeasurements: () => void;
	toggleShowStepLog: () => void;
	skipStep: () => Promise<void>;
	isActiveStep: boolean;
}

type CommentModalMode = "comment" | "suggestion";

export const StepTools: FC<StepToolsProps> = (props) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [addCommentModalState, setAddCommentModalState] = useState({
		isOpen: false,
		skipComment: false,
		defaultMode: "comment" as CommentModalMode,
	});
	const [isSkipping, setIsSkipping] = useState(false);

	const transformedBackgroundColor = props.step.BackgroundColor
		? transformColorForTheme(
			props.step.BackgroundColor,
			theme.palette.mode === "dark",
		)
		: null;

	const open = Boolean(anchorEl);
	const handleButtonClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose = () => setAnchorEl(null);

	const handleSkip = async () => {
		if (isSkipping) return;
		setIsSkipping(true);
		handleClose();
		try {
			if (props.step.IsSkipped) {
				await props.skipStep();
			} else {
				setAddCommentModalState({
					isOpen: true,
					skipComment: true,
					defaultMode: "comment",
				});
			}
		} finally {
			setIsSkipping(false);
		}
	};

	const showSkip =
		!props.step.Status &&
		(props.step.CheckpointType === CheckpointType.SATOSLive ||
			props.step.CheckpointType === CheckpointType.Checkpoint);

	return (
		<Box
			className="step-tools"
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 0.5,
				flexShrink: 0,
			}}
		>
			<Tooltip
				title={<Translate String="Checklist.Step.Advanced" HTMLTag="span" />}
				enterDelay={400}
			>
				<IconButton
					id="step-actions-button"
					aria-controls={open ? "step-actions-menu" : undefined}
					aria-expanded={open ? "true" : undefined}
					aria-haspopup="menu"
					aria-label={GetLanguageValueAsString(
						translation,
						"Auto.Features.Procedures.Ui.Components.Checklists.Checklist.Step.StepTools.L103C17.AdvancedActions",
						"Advanced actions",
					)}
					onClick={handleButtonClick}
					sx={{
						height: 44,
						width: 44,
						margin: 0,
						"&:focus-visible": {
							outline: "2px solid",
							outlineColor: "primary.main",
							outlineOffset: 2,
						},
					}}
				>
					<MoreVertIcon
						sx={{
							color: transformedBackgroundColor
								? getTextColorForBackground(transformedBackgroundColor)
								: props.step.BackgroundColor
									? getTextColorForBackground(props.step.BackgroundColor)
									: "",
						}}
					/>
				</IconButton>
			</Tooltip>
			<StyledMenu
				id="step-actions-menu"
				MenuListProps={{
					"aria-labelledby": "step-actions-button",
					dense: true,
				}}
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
			>
				<Box px={1.5} pt={1} pb={0.5}>
					<Typography
						variant="overline"
						color="text.secondary"
						sx={{ letterSpacing: 0.6 }}
					>
						<Translate String="Checklist.Step.Actions.Section" HTMLTag="span" />
					</Typography>
				</Box>
				{showSkip && (
					<MenuItem
						onClick={() => {
							void handleSkip();
						}}
						disabled={isSkipping}
					>
						<ListItemIcon>
							{isSkipping ? (
								<CircularProgress size={16} />
							) : (
								<SkipNextIcon color="action" fontSize="small" />
							)}
						</ListItemIcon>
						<ListItemText
							primary={
								isSkipping ? (
									<Translate
										String="Checklist.Tools.Skipping.Step"
										HTMLTag="span"
									>
										Updating...
									</Translate>
								) : props.step.IsSkipped ? (
									<Translate
										String="Checklist.Tools.Include.Step"
										HTMLTag="span"
									/>
								) : (
									<Translate String="Checklist.Tools.Skip.Step" HTMLTag="span" />
								)
							}
							secondary={
								<Typography variant="caption" color="text.secondary">
									{props.step.IsSkipped ? (
										<Translate
											String="Checklist.Tools.Include.Step.Desc"
											HTMLTag="span"
										/>
									) : (
										<Translate
											String="Checklist.Tools.Skip.Step.Desc"
											HTMLTag="span"
										/>
									)}
								</Typography>
							}
						/>
					</MenuItem>
				)}
				<MenuItem
					disabled={props.step.IsSkipped || props.step.Readonly}
					onClick={() => {
						handleClose();
						setAddCommentModalState({
							isOpen: true,
							skipComment: false,
							defaultMode: "comment",
						});
					}}
				>
					<ListItemIcon>
						<ChatBubbleIcon color="info" fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={
							<Translate String="Checklist.Tools.Add.Comment" HTMLTag="span" />
						}
						secondary={
							<Typography variant="caption" color="text.secondary">
								<Translate
									String="Checklist.Tools.Add.Comment.Desc"
									HTMLTag="span"
								/>
							</Typography>
						}
					/>
				</MenuItem>
				<MenuItem
					disabled={props.step.IsSkipped || props.step.Readonly}
					onClick={() => {
						handleClose();
						setAddCommentModalState({
							isOpen: true,
							skipComment: false,
							defaultMode: "suggestion",
						});
					}}
				>
					<ListItemIcon>
						<LightbulbOutlinedIcon color="warning" fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={
							<Translate String="Checklist.Tools.Add.Suggestion" HTMLTag="span">
								Suggest improvement
							</Translate>
						}
					/>
				</MenuItem>
				<MenuItem
					onClick={() => {
						handleClose();
						props.toggleShowStepLog();
					}}
				>
					<ListItemIcon>
						<WorkHistoryIcon color="primary" fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={
							<Translate String="Checklist.Tools.Show.Log" HTMLTag="span" />
						}
						secondary={
							<Typography variant="caption" color="text.secondary">
								<Translate
									String="Checklist.Tools.Show.Log.Desc"
									HTMLTag="span"
								/>
							</Typography>
						}
					/>
				</MenuItem>
			</StyledMenu>
			<Box
				className="status-actions"
				sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
			>
				<ChecklistStatus checklistId={props.checklistId} step={props.step} />
				<LiveStepStatus
					step={props.step}
					toggleShowMeasurements={props.toggleShowMeasurements}
					isActiveStep={props.isActiveStep}
				/>
				<AddComment
					checklistId={props.checklistId}
					step={props.step}
					showAddComment={addCommentModalState.isOpen}
					closeModal={() =>
						setAddCommentModalState({
							isOpen: false,
							skipComment: false,
							defaultMode: "comment",
						})
					}
					skipComment={addCommentModalState.skipComment}
					skipStep={props.skipStep}
					defaultMode={addCommentModalState.defaultMode}
				/>
			</Box>
		</Box>
	);
};
